import Axios, { AxiosInstance } from 'axios'
import { HttpError } from './common.interface'
import { authService } from './service/auth.service'

const BASE_URL = `${process.env.REACT_APP_BASE_URL}`

let axiosInstance: AxiosInstance = Axios.create({
	baseURL: BASE_URL,
	timeout: 12000,
	timeoutErrorMessage: 'Connection timeout',
})
let interceptorsConfigured = false

// ===============================================================================================
// Public Methods
// ===============================================================================================

function normalizeApiMessage(message: unknown): string {
	if (Array.isArray(message)) {
		return message.filter(Boolean).join(', ') || 'Request failed'
	}
	if (typeof message === 'string' && message.trim()) {
		return message
	}
	return 'Request failed'
}

function throwIfApiErrorPayload(data: any) {
	if (data?.message && data?.data === undefined) {
		throw new HttpError(normalizeApiMessage(data.message), String(data.statusCode ?? ''))
	}
}

function rethrowAsHttpError(error: unknown): never {
	if (error instanceof HttpError) {
		throw error
	}
	const axiosErr = error as {
		response?: { data?: { message?: unknown; statusCode?: number }; status?: number }
		message?: string
	}
	const payload = axiosErr.response?.data
	if (payload?.message) {
		throw new HttpError(
			normalizeApiMessage(payload.message),
			String(payload.statusCode ?? axiosErr.response?.status ?? ''),
		)
	}
	if (error instanceof Error) {
		throw error
	}
	throw new Error('Request failed')
}

export async function httpGet<T extends any>(
	path: string,
	queryParams?: any,
	customHeaders?: any,
	progress: boolean = true,
) {
	const accessToken = getJWTToken()
	console.log('🔑 BEARER TOKEN FOR SWAGGER:', accessToken)
	const response = await axios().get<T>(path, {
		params: queryParams,
		headers: createHeaders(customHeaders, accessToken),
		...{ progress },
	})

	const data: any = response
	// TODO: API always response ok with invalid request.
	if (data['message'] && !data['data']) {
		throw new HttpError(data['message'], data['statusCode'])
	}

	return response.data
}

export async function httpPatch<T>(
	path: string,
	body?: any,
	customHeaders?: any,
	config?: any,
	progress: boolean = true,
) {
	const accessToken = getJWTToken()
	const response = await axios().patch<T>(path, body, {
		...config,
		...{ progress },
		headers: createHeaders(customHeaders, accessToken),
	})
	const data: any = response.data

	// // TODO: API always response ok with invalid request.
	if (data['message'] && !data['data']) {
		throw new HttpError(data['message'], data['statusCode'])
	}

	return data
}
export async function httpPost<T>(
	path: string,
	body?: any,
	customHeaders?: any,
	config?: any,
	progress: boolean = true,
) {
	const accessToken = getJWTToken()
	const response = await axios().post<T>(path, body, {
		...config,
		...{ progress },
		headers: createHeaders(customHeaders, accessToken),
	})
	const data: any = response.data
	// // TODO: API always response ok with invalid request.
	if (data['message'] && !data['data']) {
		throw new HttpError(data['message'], data['statusCode'])
	}

	return data
}

export async function httpPut<T>(
	path: string,
	body?: any,
	customHeaders?: any,
	progress: boolean = true,
) {
	const accessToken = getJWTToken()
	const response = await axios().put<T>(path, body, {
		headers: createHeaders(customHeaders, accessToken),
		...{ progress },
	})
	const data: any = response.data
	if (data['message'] && !data['data']) {
		throw new HttpError(data['message'], data['statusCode'])
	}
	return data
}

export async function httpDelete<T>(
	path: string,
	queryParams?: any,
	customHeaders?: any,
	progress: boolean = true,
) {
	const accessToken = getJWTToken()
	try {
		const response = await axios().delete<T>(path, {
			params: queryParams,
			headers: createHeaders(customHeaders, accessToken),
			...{ progress },
		})
		const data: any = response.data
		throwIfApiErrorPayload(data)
		return data
	} catch (error: unknown) {
		rethrowAsHttpError(error)
	}
}

// ===============================================================================================
// Private Methods
// ===============================================================================================

function createHeaders(customHeaders?: any, accessToken?: string | null) {
	const headers = {
		'Content-Type': 'application/json',
		Authorization: `Bearer ${accessToken || ''}`,
	}

	return {
		...headers,
		...customHeaders,
	}
}

function getJWTToken(): string | null {
	return authService.getToken()
}

export const axios = () => {
	if (!axiosInstance) {
		axiosInstance = Axios.create({
			baseURL: BASE_URL,
			timeout: 12000,
			timeoutErrorMessage: 'Connection timeout',
		})
	}

	if (!interceptorsConfigured) {
		axiosInstance.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalRequest = error.config

				if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
					return Promise.reject(error)
				}

				originalRequest._retry = true
				const newAccessToken = await authService.refreshToken()

				if (!newAccessToken) {
					return Promise.reject(error)
				}

				axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`
				originalRequest.headers = {
					...(originalRequest.headers || {}),
					Authorization: `Bearer ${newAccessToken}`,
				}

				return axiosInstance(originalRequest)
			},
		)

		interceptorsConfigured = true
	}

	return axiosInstance
}
